<template>
  <div class="intro-page-new">
    <!-- Navigation -->
    <nav class="navbar" :class="{ 'scrolled': isScrolled }">
      <div class="container nav-container">
        <div class="brand">
          <img src="@/assets/owl-logo.png" alt="Owl Logo" class="brand-logo" />
          <span class="brand-text">OWL <span class="highlight">ANALYSIS</span></span>
        </div>
        <div class="nav-links">
          <a href="#features" @click.prevent="scrollTo('features')">核心功能</a>
          <a href="#how-it-works" @click.prevent="scrollTo('how-it-works')">分析流程</a>
          <a href="#about" @click.prevent="scrollTo('about')">关于我们</a>
        </div>
        <div class="nav-actions">
          <!-- 未登录：显示登录和注册 -->
          <template v-if="!authStore.isAuthenticated">
            <Button type="text" ghost class="login-btn" @click="goToLogin">登录</Button>
            <Button type="primary" shape="circle" class="register-btn" @click="goToRegister">立即注册</Button>
          </template>
          <!-- 已登录：显示控制台 -->
          <template v-else>
            <Button type="primary" shape="circle" class="console-btn" @click="goToConsole">
              <Icon type="md-apps" />
              控制台
            </Button>
          </template>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-bg">
        <div class="glow-orb orb-1"></div>
        <div class="glow-orb orb-2"></div>
        <div class="glow-orb orb-3"></div>
        <div class="grid-overlay"></div>
      </div>

      <div class="container hero-container">
        <div class="hero-content">
          <div class="hero-badge" data-aos="fade-down">
            <span class="pulse-dot"></span>
            AI 驱动的智能投资分析系统
          </div>
          <h1 class="hero-title" data-aos="fade-up" data-aos-delay="100">
            洞察企业背后的<br />
            <span class="text-gradient title-offset">真实价值</span>
          </h1>
          <p class="hero-subtitle" data-aos="fade-up" data-aos-delay="200">
            告别繁琐的数据整理。Owl 利用先进的 AI 算法，深度剖析企业，为您提供专业级的估值分析与数据参考。
          </p>
          <div class="hero-btns" data-aos="fade-up" data-aos-delay="300">
            <Button type="primary" size="large" class="cta-btn" @click="goToLogin">
              开始分析
              <Icon type="ios-arrow-forward" />
            </Button>
            <Button type="text" ghost size="large" class="demo-btn" @click="scrollTo('features')">
              <Icon type="ios-play-outline" />
              了解更多
            </Button>
          </div>

          <div class="hero-stats" data-aos="fade-up" data-aos-delay="400">
            <div class="stat-item">
              <div class="stat-val">5,000+</div>
              <div class="stat-label">覆盖公司</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-val">5,000+</div>
              <div class="stat-label">报告数量</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-val">实时</div>
              <div class="stat-label">数据更新</div>
            </div>
          </div>
        </div>

        <div class="hero-visual" data-aos="fade-left" data-aos-delay="200">
          <div class="glass-card main-card">
            <div class="card-header">
              <div class="window-controls">
                <span></span><span></span><span></span>
              </div>
              <div class="header-text">价值偏离度分析</div>
            </div>
            <div class="card-body">
              <!-- Simulated Chart Area -->
              <div class="chart-area">
                <svg viewBox="0 0 400 200" preserveAspectRatio="none" width="100%" height="100%">
                  <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="rgba(0, 242, 96, 0.15)" />
                      <stop offset="100%" stop-color="rgba(0, 242, 96, 0)" />
                    </linearGradient>
                  </defs>
                  
                  <!-- Intrinsic Value Curve (Dashed, Steady Growth) -->
                  <path d="M0 170 Q 200 130 400 80" stroke="rgba(255,255,255,0.25)" stroke-width="2" stroke-dasharray="6 4" fill="none" />
                  <text x="360" y="65" fill="rgba(255,255,255,0.4)" font-size="10" text-anchor="middle">内在价值</text>
                  
                  <!-- Market Price Line (Solid, Fluctuating) -->
                  <!-- Extended to x=400, finishing at y=120 to stay undervalued (Value is at 80) -->
                  <path d="M0 170 C 60 170, 100 60, 180 110 S 320 170, 400 120" stroke="#00f260" stroke-width="3" fill="none" stroke-linecap="round" class="chart-path-anim" />
                  
                  <!-- Fill Area under Price Line -->
                  <path d="M0 170 C 60 170, 100 60, 180 110 S 320 170, 400 120 V 200 H 0 Z" fill="url(#chartGradient)" />

                  <!-- Deviation Indicator (Vertical Line) at x=380 -->
                  <!-- At x=380, Value y is approx 85, Price y is approx 128 -->
                  <line x1="380" y1="87" x2="380" y2="128" stroke="#00f260" stroke-width="1" stroke-dasharray="3 3" opacity="0.6" />
                  
                  <!-- Current Price Point -->
                  <circle cx="380" cy="128" r="5" fill="#0f172a" stroke="#00f260" stroke-width="2" class="chart-dot" />
                  <text x="380" y="148" fill="#00f260" font-size="10" font-weight="bold" text-anchor="middle">低估区间</text>
                </svg>
              </div>
              <div class="data-row">
                <div class="data-item">
                  <span class="label">最新估值</span>
                  <span class="value">¥45.20</span>
                </div>
                <div class="data-item">
                  <span class="label">当前价格</span>
                  <span class="value highlight">¥32.80</span>
                </div>
                <div class="data-item">
                  <span class="label">偏离度</span>
                  <span class="value success">-27.4%</span>
                </div>
              </div>
            </div>

            <!-- Floating Elements -->
            <div class="float-card float-1">
              <Icon type="md-trending-up" />
              <span>低估值机会</span>
            </div>
            <div class="float-card float-2">
              <Icon type="md-checkmark-circle-outline" />
              <span>财务健康 A+</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <div class="container">
        <div class="section-header center">
          <h2 class="section-title">核心功能</h2>
          <p class="section-desc">不仅是数据罗列，更是深度的商业洞察</p>
        </div>

        <div class="features-grid">
          <div class="feature-box">
            <div class="feature-icon">
              <Icon type="md-analytics" />
            </div>
            <h3>智能财报诊断</h3>
            <p>三张报表自动联动分析，快速识别财务造假风险，一键生成体检报告。</p>
          </div>
          <div class="feature-box">
            <div class="feature-icon">
              <Icon type="ios-flask" />
            </div>
            <h3>价值估值模型</h3>
            <p>内置多种经典估值模型（DCF, PE, PB），并结合行业特性自动计算合理价值区间。</p>
          </div>
          <div class="feature-box">
            <div class="feature-icon">
              <Icon type="md-locate" />
            </div>
            <h3>企业画像定位</h3>
            <p>基于投资大师对企业的定位，将企业精准划分为六大类型，匹配最佳投资策略。</p>
          </div>
          <div class="feature-box">
            <div class="feature-icon">
              <Icon type="md-git-network" />
            </div>
            <h3>护城河分析</h3>
            <p>AI 辅助分析企业竞争优势，定性与定量结合，判断企业长期竞争力。</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section id="how-it-works" class="steps-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">分析流程</h2>
        </div>

        <div class="steps-container">
          <div class="step-item">
            <div class="step-number">01</div>
            <div class="step-content">
              <h4>输入代码</h4>
              <p>输入股票代码，支持A股及港股主流公司。</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">02</div>
            <div class="step-content">
              <h4>AI 运算</h4>
              <p>系统自动抓取最新财报，进行上百项指标计算。</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">03</div>
            <div class="step-content">
              <h4>生成报告</h4>
              <p>即时生成包含估值、风险、成长性的深度报告。</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">04</div>
            <div class="step-content">
              <h4>辅助分析</h4>
              <p>基于价值偏离度，辅助您进行投资分析参考。</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-box">
          <div class="cta-content">
            <h2>准备好开始智能投资之旅了吗？</h2>
            <p>加入成千上万的价值投资者，让数据助力您的分析。</p>
            <Button type="primary" size="large" class="cta-btn-large" @click="goToRegister">立即免费注册</Button>
          </div>
        </div>
      </div>
    </section>

    <!-- About Us Section -->
    <section id="about" class="about-section">
      <div class="container">
        <div class="about-wrapper">
          <div class="about-content" data-aos="fade-right">
            <div class="section-tag">关于我们</div>
            <h2 class="about-title">坚守<span class="text-gradient">价值投资</span>的信仰</h2>
            <blockquote class="quote">
              "Price is what you pay. Value is what you get."
              <footer>— Warren Buffett</footer>
            </blockquote>
            <p class="about-text">
              在喧嚣的市场中，我们始终相信：<strong>价格终将回归价值</strong>。Owl 不仅仅是一个数据工具，它是我们为理性投资者打造的一座灯塔。
            </p>
            <p class="about-text">
              我们是一支专注于金融数据技术的团队。我们致力于运用 AI 技术和数据分析能力，为投资者提供财务数据整理与可视化工具，帮助您更高效地理解复杂的财务报表。
            </p>
            <div class="values-list">
              <div class="value-item">
                <Icon type="md-infinite" />
                <span>长期主义</span>
              </div>
              <div class="value-item">
                <Icon type="md-lock" />
                <span>安全边际</span>
              </div>
              <div class="value-item">
                <Icon type="md-sunny" />
                <span>复利思维</span>
              </div>
            </div>
          </div>
          <div class="about-visual" data-aos="fade-left">
            <div class="visual-card">
              <div class="card-glow"></div>
              <div class="card-icon">
                <Icon type="md-trophy" />
              </div>
              <h3>我们的使命</h3>
              <p>让每一位投资者都能像大师一样思考，像机构一样审慎。</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import AppFooter from '@/components/common/AppFooter.vue'

const router = useRouter()
const authStore = useAuthStore()
const isScrolled = ref(false)

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

const scrollTo = (id: string) => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

const goToLogin = () => {
  router.push('/login')
}

const goToRegister = () => {
  router.push('/register')
}

const goToConsole = () => {
  router.push('/app/home')
}
</script>

<style scoped>
/* Intro Page */
.intro-page-new {
  background-color: #0f172a;
  color: #f8fafc;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Navbar */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 20px 0;
  transition: all 0.3s ease;
}

.navbar.scrolled {
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(12px);
  padding: 15px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: 0.5px;
}

.brand-logo {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
}

.brand-text .highlight {
  color: #00f260;
}

.nav-links {
  display: flex;
  gap: 30px;
}

.nav-links a {
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.95rem;
  transition: color 0.3s;
}

.nav-links a:hover {
  color: #fff;
}

.nav-actions {
  display: flex;
  gap: 15px;
  align-items: center;
}

.login-btn {
  color: #fff !important;
}

.register-btn {
  background: linear-gradient(90deg, #00f260, #0575e6) !important;
  border: none !important;
  font-weight: 600;
  padding: 0 24px;
}

.console-btn {
  background: linear-gradient(90deg, #00f260, #0575e6) !important;
  border: none !important;
  font-weight: 600;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Hero Section */
.hero-section {
  position: relative;
  padding: 180px 0 100px;
  min-height: 100vh;
  display: flex;
  align-items: center;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: #0575e6;
  top: -100px;
  right: -100px;
  animation: floatOrb 10s infinite ease-in-out;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: #00f260;
  bottom: 100px;
  left: -50px;
  animation: floatOrb 12s infinite ease-in-out reverse;
}

.orb-3 {
  width: 200px;
  height: 200px;
  background: #8e2de2;
  top: 40%;
  left: 40%;
  opacity: 0.2;
  animation: pulseOrb 8s infinite ease-in-out;
}

.grid-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
}

.hero-container {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #00f260;
  margin-bottom: 24px;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background: #00f260;
  border-radius: 50%;
  box-shadow: 0 0 10px #00f260;
  animation: pulse 2s infinite;
}

.hero-title {
  font-size: 3.5rem;
  line-height: 1.2;
  font-weight: 800;
  margin-bottom: 24px;
}

.text-gradient {
  background: linear-gradient(135deg, #00f260 0%, #0575e6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.title-offset {
  display: inline-block;
  padding-left: 30px;
}

.hero-subtitle {
  font-size: 1.1rem;
  color: #94a3b8;
  line-height: 1.6;
  margin-bottom: 40px;
  max-width: 90%;
}

.hero-btns {
  display: flex;
  gap: 20px;
  margin-bottom: 60px;
}

.cta-btn {
  background: #fff !important;
  color: #0f172a !important;
  border: none !important;
  font-weight: 700 !important;
  padding: 0 32px !important;
  height: 50px !important;
  border-radius: 25px !important;
  transition: transform 0.2s !important;
}

.cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(255, 255, 255, 0.1);
}

.demo-btn {
  color: #fff !important;
  height: 50px !important;
}

.hero-stats {
  display: flex;
  align-items: center;
  gap: 30px;
}

.stat-val {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
}

.stat-label {
  font-size: 0.85rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
}

/* Hero Visual */
.hero-visual {
  position: relative;
}

.glass-card {
  background: rgba(30, 41, 59, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.main-card {
  position: relative;
  width: 100%;
  aspect-ratio: 16/10;
  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.window-controls {
  display: flex;
  gap: 6px;
}

.window-controls span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
}

.header-text {
  font-size: 0.9rem;
  color: #94a3b8;
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chart-area {
  flex: 1;
  position: relative;
  margin-bottom: 20px;
  background: linear-gradient(180deg, rgba(0, 242, 96, 0.02) 0%, transparent 100%);
  border-radius: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.chart-dot {
  filter: drop-shadow(0 0 4px rgba(0, 242, 96, 0.5));
  animation: pulseDot 3s infinite ease-in-out;
}

.dot-1 { animation-delay: 0s; }
.dot-2 { animation-delay: 1s; }
.dot-3 { animation-delay: 2s; }

@keyframes pulseDot {
  0%, 100% { stroke-width: 2; r: 5; }
  50% { stroke-width: 3; r: 6; }
}

.data-row {
/* ... rest of the styles ... */  display: flex;
  justify-content: space-between;
  background: rgba(0, 0, 0, 0.2);
  padding: 16px;
  border-radius: 12px;
}

.data-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.data-item .label {
  font-size: 0.75rem;
  color: #64748b;
}

.data-item .value {
  font-size: 1.1rem;
  font-weight: 600;
}

.data-item .highlight {
  color: #00f260;
}

.data-item .success {
  color: #00f260;
}

.float-card {
  position: absolute;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.15);
  padding: 12px 20px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: float 6s infinite ease-in-out;
}

.float-1 {
  top: -30px;
  right: -20px;
  color: #00f260;
}

.float-2 {
  bottom: 40px;
  left: -40px;
  color: #0575e6;
  animation-delay: 3s;
}

/* Features */
.features-section {
  padding: 100px 0;
  background: #0f172a;
  scroll-margin-top: 80px;
}

.section-header {
  margin-bottom: 60px;
}

.section-header.center {
  text-align: center;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.section-desc {
  color: #94a3b8;
  font-size: 1.1rem;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.feature-box {
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 40px 30px;
  border-radius: 20px;
  transition: all 0.3s ease;
}

.feature-box:hover {
  background: rgba(30, 41, 59, 0.8);
  transform: translateY(-10px);
  border-color: #00f260;
}

.feature-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, rgba(0, 242, 96, 0.1), rgba(5, 117, 230, 0.1));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #00f260;
  margin-bottom: 24px;
}

.feature-box h3 {
  font-size: 1.25rem;
  margin-bottom: 12px;
}

.feature-box p {
  color: #94a3b8;
  line-height: 1.6;
}

/* Steps */
.steps-section {
  padding: 100px 0;
  background: rgba(30, 41, 59, 0.3);
  scroll-margin-top: 80px;
}

.steps-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  margin-top: 60px;
}

.step-item {
  position: relative;
}

.step-number {
  font-size: 4rem;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.03);
  position: absolute;
  top: -30px;
  left: 0;
  line-height: 1;
}

.step-content {
  position: relative;
  z-index: 1;
  padding-top: 20px;
}

.step-content h4 {
  font-size: 1.25rem;
  margin-bottom: 10px;
  color: #fff;
}

.step-content p {
  color: #94a3b8;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* CTA */
.cta-section {
  padding: 100px 0;
}

.cta-box {
  background: linear-gradient(135deg, #00f260 0%, #0575e6 100%);
  border-radius: 30px;
  padding: 80px 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 1;
}

.cta-content h2 {
  font-size: 2.5rem;
  margin-bottom: 20px;
  color: #fff;
}

.cta-content p {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
}

.cta-btn-large {
  background: #fff !important;
  color: #0575e6 !important;
  font-weight: 700 !important;
  height: 56px !important;
  padding: 0 48px !important;
  font-size: 1.1rem !important;
  border-radius: 28px !important;
  border: none !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
}

/* Animations */
@keyframes floatOrb {

  0%,
  100% {
    transform: translate(0, 0);
  }

  50% {
    transform: translate(30px, 50px);
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 242, 96, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(0, 242, 96, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(0, 242, 96, 0);
  }
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-15px);
  }
}

/* About Section */
.about-section {
  padding: 120px 0;
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
  position: relative;
  scroll-margin-top: 80px;
}

.about-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.section-tag {
  display: inline-block;
  color: #00f260;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 16px;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.about-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 32px;
  line-height: 1.2;
}

.quote {
  border-left: 4px solid #00f260;
  padding-left: 20px;
  font-size: 1.25rem;
  font-style: italic;
  color: #cbd5e1;
  margin-bottom: 32px;
  background: rgba(255, 255, 255, 0.02);
  padding: 16px 20px;
  border-radius: 0 8px 8px 0;
}

.quote footer {
  display: block;
  margin-top: 8px;
  font-size: 0.9rem;
  color: #64748b;
  font-style: normal;
  font-weight: 600;
}

.about-text {
  color: #94a3b8;
  font-size: 1.05rem;
  line-height: 1.8;
  margin-bottom: 24px;
}

.about-text strong {
  color: #f8fafc;
}

.values-list {
  display: flex;
  gap: 24px;
  margin-top: 40px;
}

.value-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f8fafc;
  font-weight: 600;
}

.value-item i {
  color: #00f260;
  font-size: 1.2rem;
}

.visual-card {
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 30px;
  padding: 60px 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.card-glow {
  position: absolute;
  width: 200px;
  height: 200px;
  background: #0575e6;
  filter: blur(100px);
  opacity: 0.2;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.card-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #00f260 0%, #0575e6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: #fff;
  margin: 0 auto 32px;
  position: relative;
  z-index: 1;
  box-shadow: 0 10px 30px rgba(5, 117, 230, 0.3);
}

.visual-card h3 {
  font-size: 1.5rem;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

.visual-card p {
  color: #94a3b8;
  font-size: 1.1rem;
  line-height: 1.6;
  position: relative;
  z-index: 1;
}

/* Responsive */
@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  /* Navbar */
  .navbar {
    padding: 15px 0;
  }
  
  .nav-links {
    display: none; /* Hide navigation links on mobile for simplicity */
  }

  .nav-actions .login-btn {
    font-size: 14px;
  }

  .nav-actions .register-btn, .nav-actions .console-btn {
    padding: 0 16px;
    font-size: 14px;
  }

  /* Hero */
  .hero-section {
    padding: 120px 0 60px;
    min-height: auto;
  }

  .hero-container {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 40px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .title-offset {
    padding-left: 15px;
  }

  .hero-subtitle {
    font-size: 1rem;
    margin-left: auto;
    margin-right: auto;
  }

  .hero-btns {
    justify-content: center;
    flex-direction: column;
    gap: 16px;
  }

  .cta-btn, .demo-btn {
    width: 100%;
  }

  .hero-stats {
    justify-content: center;
    gap: 15px;
    flex-wrap: wrap;
  }

  .stat-val {
    font-size: 1.25rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .hero-visual {
    margin-top: 20px;
  }

  .main-card {
    padding: 16px;
  }

  .float-card {
    display: none; /* Hide floating elements on mobile to reduce clutter */
  }

  /* Features */
  .features-section {
    padding: 60px 0;
  }

  .section-title {
    font-size: 2rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .feature-box {
    padding: 30px 20px;
  }

  /* Steps */
  .steps-section {
    padding: 60px 0;
  }

  .steps-container {
    grid-template-columns: 1fr;
    gap: 30px;
    margin-top: 40px;
  }

  .step-item {
    text-align: center;
  }

  .step-number {
    position: static;
    margin-bottom: 10px;
    font-size: 3rem;
  }

  /* CTA */
  .cta-section {
    padding: 60px 0;
  }

  .cta-box {
    padding: 40px 20px;
  }

  .cta-content h2 {
    font-size: 1.8rem;
  }

  .cta-btn-large {
    width: 100%;
    padding: 0 20px !important;
  }

  /* About */
  .about-section {
    padding: 60px 0;
  }

  .about-wrapper {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .about-title {
    font-size: 2rem;
  }

  .visual-card {
    padding: 40px 24px;
  }

  .values-list {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
}
</style>
